import type { ApiErrorDetails, ApiErrorResponse } from '@travel-crm/types';

export class ApiClientError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly requestId?: string,
    public readonly details: ApiErrorDetails = [],
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

const csrfCookieName = import.meta.env.VITE_CSRF_COOKIE_NAME ?? 'travel_crm_csrf';
const unsafeMethods = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function readCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const prefix = `${encodeURIComponent(name)}=`;
  const item = document.cookie.split('; ').find((cookie) => cookie.startsWith(prefix));
  return item === undefined ? undefined : decodeURIComponent(item.slice(prefix.length));
}

export async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const headers = new Headers(init.headers);
  headers.set('Accept', 'application/json');
  if (
    init.body !== undefined &&
    !(init.body instanceof FormData) &&
    !headers.has('Content-Type')
  ) {
    headers.set('Content-Type', 'application/json');
  }
  if (unsafeMethods.has(method)) {
    const csrfToken = readCookie(csrfCookieName);
    if (csrfToken !== undefined) headers.set('X-CSRF-Token', csrfToken);
  }

  const response = await fetch(path, {
    ...init,
    method,
    credentials: 'same-origin',
    headers,
  });

  if (!response.ok) {
    let payload: ApiErrorResponse | undefined;
    try {
      payload = (await response.json()) as ApiErrorResponse;
    } catch {
      payload = undefined;
    }
    throw new ApiClientError(
      response.status,
      payload?.error.code ?? 'HTTP_ERROR',
      payload?.error.message ?? 'The request failed.',
      payload?.error.requestId,
      payload?.error.details ?? [],
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}
